import { produce } from 'immer';

import PENDING_TYPE from '@app/constants/pendingType';
import {
  SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST,
  SIGNING_PROGRESS_INFORMATION__FETCH_SUCCESS,
} from '@app/store/actionTypes';
import { CarbonCopyContact } from '@app/store/contract-setup/reducers/carbonCopies';
import Status from '@app/store/document/Status';
import Requester from '@app/store/documents/Requester';
import { SigningProgressInformationAction } from '@app/store/signingProgressInformation/actions';

interface PendingCreator {
  name: string;
  contactInfo: string;
}

export interface SigningProgressInformationDocumentState {
  carbonCopies: CarbonCopyContact[];
  documentId: string | null;
  originHash: string | null;
  name: string | null;
  pending: {
    pendingCreator: PendingCreator;
    participantId?: string | null;
    pendedAt?: string;
    pendingType?: string;
    reason?: string;
  } | null;
  requester: Requester | null;
  status: string | null;
  statusWithPending: string | null;
}

const initialState: SigningProgressInformationDocumentState = {
  carbonCopies: [],
  documentId: null,
  originHash: null,
  name: null,
  pending: null,
  requester: null,
  statusWithPending: null,
  status: null,
};

export default function reducer(
  state: SigningProgressInformationDocumentState = initialState,
  action: SigningProgressInformationAction
) {
  switch (action.type) {
    case SIGNING_PROGRESS_INFORMATION__FETCH_REQUEST:
      return initialState;
    case SIGNING_PROGRESS_INFORMATION__FETCH_SUCCESS:
      return produce(state, (draft) => {
        if (action.payload) {
          const {
            carbonCopies,
            documentId,
            participants,
            status,
            name,
            requester,
            pending,
            originHash,
          } = action.payload.document;

          const pendingCreator = {
            name: '',
            contactInfo: '',
          };

          if (pending && pending.participantId) {
            const pendingParticipant = participants.find(
              (participant) => participant.participantId === pending.participantId
            );
            pendingCreator.name = pendingParticipant?.name ?? '';
            pendingCreator.contactInfo = pendingParticipant?.signingMethod.contactInfo ?? '';
          } else if (pending) {
            pendingCreator.name = requester.name;
            pendingCreator.contactInfo = requester.email;
          }

          const pendingState = {
            ...pending,
            pendingCreator,
          };

          let statusWithPending = '서명 진행 중';
          if (status === Status.COMPLETED) {
            statusWithPending = '완료됨';
          } else if (status === Status.ON_PROCESSING) {
            statusWithPending = '처리 중';
          } else if (status === Status.PROCESSING_FAILED) {
            statusWithPending = '처리 실패';
          } else if (status === Status.SCHEDULE_READY) {
            statusWithPending = '전송 예약';
          } else if (status === Status.SCHEDULE_CANCELED) {
            statusWithPending = '예약 취소';
          } else if (status === Status.SCHEDULE_START_FAILED) {
            statusWithPending = '예약 전송 실패';
          } else if (status === Status.PENDING) {
            if (pending?.pendingType === PENDING_TYPE.REJECTION) {
              statusWithPending = '거절됨';
            }
            if (pending?.pendingType === PENDING_TYPE.REQUEST_CANCELLATION) {
              statusWithPending = '서명 요청 취소됨';
            }
            if (pending?.pendingType === PENDING_TYPE.SIGNING_CANCELLATION) {
              statusWithPending = '서명 취소됨';
            }
          }

          draft.carbonCopies = carbonCopies;
          draft.documentId = documentId;
          draft.originHash = originHash;
          draft.statusWithPending = statusWithPending;
          draft.status = status;
          draft.name = name;
          draft.requester = requester;
          draft.pending = pendingState;
        }
      });
    default:
      return state;
  }
}
